import { maybe, rd } from '@passionware/monads';
import { SimpleReadOnlyEvent } from '@passionware/simple-event';
import { QueryClient, useQueries, useQuery } from '@tanstack/react-query';
import { Resource } from '__types__/v1/models';
import { throttle } from 'lodash';
import { Batcher, createBatcher } from '../../../../v4/entities/common/batcher';
import { V1Action } from 'v4/migration/actionAsEventMiddleware';
import {
  ResourceApi,
  ResourceListQuery,
  ResourceQuery
} from '../../../api/resource/resource.api';
import { asGlobalResourceQuery } from '../../../api/_common/query/workspace-scoping';
import { ensureIdleQuery } from '../../../platform/ensureIdleQuery';
import { rowLevelValidation } from '../../../platform/row-level-validation';
import { WithMessageService } from '../../internal/MessageService/MessageService';
import { ResourceService } from './ResourceService';
import { WithErrorService } from '../../front/ErrorService/ErrorService';
import { WithServices } from '../../../platform/ts/services';

function normalizeResourceListQuery(query: ResourceListQuery): ResourceQuery {
  if ('workspaceId' in query) {
    return query;
  }
  return asGlobalResourceQuery(query);
}

export function createResourceService(
  config: WithServices<[WithErrorService, WithMessageService]> & {
    api: ResourceApi;
    queryClient: QueryClient;
    onCacheDump: (resources: Resource[]) => void;
    v1Event: SimpleReadOnlyEvent<V1Action>;
  }
): ResourceService {
  const {
    api,
    queryClient,
    onCacheDump,
    v1Event,
    services: { errorService, messageService }
  } = config;
  const interceptor = errorService.withCategory('ResourceService');

  // Subscribe to system effects to handle cache invalidation on mutations
  messageService.reportSystemEffect.subscribeToRequest(async message => {
    for (const effect of message.request.scope) {
      switch (effect.entity) {
        case 'resource': {
          // Invalidate all resource list queries
          queryClient.removeQueries({
            queryKey: ['resources-v5', 'list'],
            type: 'inactive'
          });
          await queryClient.invalidateQueries({
            queryKey: ['resources-v5', 'list'],
            type: 'active'
          });
          // Invalidate the specific item query
          await queryClient.invalidateQueries({
            queryKey: ['resources-v5', 'item', effect.entityId]
          });
        }
      }
    }
    message.sendResponse();
  });

  const batcher: Batcher<Resource['id'], Resource[], Resource> = createBatcher(
    async ids => {
      if (ids.length === 0) {
        return [];
      }
      return await interceptor.wrap(() => api.getItems(ids))();
    },
    (response, id) => {
      // Find the resource in the batch response
      return maybe.getOrThrow(
        maybe.of(response.find(r => r.id === id)),
        `Resource with id ${id} not found`
      );
    }
  );

  const invalidateAll = throttle(
    async () => {
      queryClient.removeQueries({
        queryKey: ['resources-v5'],
        type: 'inactive'
      });
      await queryClient.invalidateQueries({
        queryKey: ['resources-v5'],
        type: 'active'
      });
    },
    // we don't want to invalidate the cache too often
    3000,
    {
      // after period of inactivity, we want to invalidate the cache immediately
      leading: true,
      // we want to make sure that last invalidation is executed
      trailing: true
    }
  );

  v1Event.addListener(async event => {
    switch (event.type) {
      case 'CREATE_CONTACT_SUCCESS':
      case 'UPDATE_CONTACT_SUCCESS':
      case 'ARCHIVE_CONTACT_SUCCESS':
      case 'UNARCHIVE_CONTACT_SUCCESS':
        await invalidateAll();
        return;
    }
  });

  // Subscribe to cache events for guest shortlist data
  messageService.reportCache.addListener(event => {
    for (const entity of event.entities) {
      switch (entity.entity) {
        case 'resource':
          queryClient.setQueryData(
            ['resources-v5', 'item', entity.payload.id],
            entity.payload
          );
          break;
      }
    }
  });

  return {
    useList: query =>
      ensureIdleQuery(
        query,
        useQuery(
          {
            queryKey: ['resources-v5', 'list', query],
            queryFn: interceptor.wrap(async ctx => {
              const normalizedQuery = normalizeResourceListQuery(query!);
              const result = await api.getList(normalizedQuery, ctx.signal);
              const validItems = rowLevelValidation.validDataFromRows(
                result.items
              );
              onCacheDump(validItems);
              for (const resource of validItems) {
                queryClient.setQueryData(
                  ['resources-v5', 'item', resource.id],
                  resource
                );
              }
              return result;
            }),
            enabled: maybe.isPresent(query)
          },
          queryClient
        )
      ),
    ensureList: query =>
      queryClient.fetchQuery({
        queryKey: ['resources-v5', 'list', query],
        queryFn: interceptor.wrap(ctx =>
          api
            .getList(normalizeResourceListQuery(query), ctx.signal)
            .then(value => {
              onCacheDump(rowLevelValidation.validDataFromRows(value.items));
              return value;
            })
        )
      }),
    useItem: id =>
      ensureIdleQuery(
        id,
        useQuery(
          {
            queryKey: ['resources-v5', 'item', id],
            queryFn: async ({ signal }: { signal: AbortSignal }) => {
              if (!id) {
                throw new Error('Resource ID is required');
              }

              const batched = await batcher.fetch({
                payload: id,
                signal
              });
              if (!batched) {
                throw new Error(`Resource with id ${id} not found`);
              }
              onCacheDump([batched]);
              return batched;
            },
            enabled: maybe.isPresent(id)
          },
          queryClient
        )
      ),
    ensureItem: id =>
      queryClient.fetchQuery({
        queryKey: ['resources-v5', 'item', id],
        queryFn: async ({ signal }: { signal: AbortSignal }) => {
          const batched = await batcher.fetch({
            payload: id,
            signal
          });
          if (!batched) {
            throw new Error(`Resource with id ${id} not found`);
          }
          return batched;
        }
      }),
    useItemsById: ids =>
      rd.combineAll(
        useQueries({
          queries: ids.map(id => ({
            queryKey: ['resources-v5', 'item', id],
            queryFn: async ({ signal }: { signal: AbortSignal }) => {
              const batched = await batcher.fetch({
                payload: id,
                signal
              });
              if (!batched) {
                throw new Error(`Resource with id ${id} not found`);
              }
              onCacheDump([batched]);
              return batched;
            }
          }))
        })
      )
  };
}
